/**
 * Hover help for the studio's Advanced mode: one line under every section header
 * and a tooltip for every value-bound row.
 *
 * Pure data. Keys are the section ids and `${sectionId}.${rowId}` pairs from the
 * section files in this folder (section ids are globally unique, row ids unique
 * within a section). Descriptions were written against the row bindings and the
 * viewer implementation, not the labels, so where a control is inert in our
 * pipeline the text says so. A `default` is present only where it was verified
 * in the viewer source or the captured iJewel checklist
 * (docs/verify/ijewel-field-parity-checklist.md); where the two disagree the
 * engine value comes first and the iJewel one follows in brackets.
 */

/** One line under each section header: what the section does VISUALLY / practically. */
export const SECTION_HELP: Record<string, string> = {
  // ── Viewer ──
  renderer: "Canvas output size and supersampling: follow the window, or render at a fixed width and height with a scale multiplier.",
  camera: "Lens, framing and orbit limits of the viewer camera, plus the full orbit-control settings.",
  "drop-options": "How dropped or opened model files are centred and scaled as they enter the scene.",

  // ── Picking ──
  hierarchy: "Every node in the loaded scene. Click one to select it; the eye hides it without deleting it.",
  materialLibrary: "Apply a complete preset material (metal, stone and more) to the selected part.",
  savedMaterials: "Your workspace's own saved metals and stones, captured from the selected part.",
  diamonds: "Scene-wide stone settings: the gem environment and its rotation, extra environment slots, and converting parts to or from the diamond shader.",
  transform: "Move, rotate and scale gizmo for the selected part.",
  pivotControls: "A pivot-based transform gizmo with snapping for translation, rotation and scale.",
  pivotEdit: "Move the pivot point of the selected part, e.g. to its centre, bottom or the origin.",
  picker: "How clicking in the viewport selects parts: on or off, focus on pick, multi-select.",
  uvUnwrap: "Generates a second UV set for the selected mesh. Here it is a simple box projection, not true xAtlas chart packing.",
  widgets: "On-canvas helper gizmos, such as the light-direction helper.",
  material: "Quick metal controls for the selected part: colour, polish and reflection strength.",
  nodeInspector: "Position, rotation, scale and render flags of the selected node.",
  nodeGeometry: "Vertex, triangle and mesh counts for the selected node and everything under it.",
  diamondRayTracing: "Optical settings of the selected stone's ray-traced gem shader: body colour, bounces, refraction, fire and brightness.",
  materialAdvanced: "Every physical-material field of the selected metal part, beyond the short list in Material.",
  materialMaps: "Texture map slots (colour, roughness, normal, bump and more) for the selected part's material.",
  materialExtensions: "Extra shading features on the selected material, including the layered cavity (worn patina) effect.",

  // ── Scene ──
  savedEnvironments: "Save and re-apply your workspace's lighting set-ups: backdrop, environment strength and rotation, exposure and the light rig.",
  environment: "The HDR environment that lights the scene and shows in reflections, and the backdrop behind the piece.",
  bgEnvAdvanced: "Full backdrop and environment control, including a second environment slot and using the environment as the backdrop.",
  groundFull: "The floor under the piece: visibility, height, baked contact shadow and optional mirror reflection.",
  progressiveShadow: "A crisp directional light whose soft shadow refines on idle. It edits the same settings as the Progressive Shadow block inside Ground.",
  randomizedSelfShadow: "Softens the shadows the key light casts on the jewellery itself by jittering the light across idle refinement frames.",
  gradientSvg: "Generates a linear or radial two-colour gradient to use as the backdrop.",
  hdriGround: "Projects the environment HDR onto a ground dome, so the piece sits inside the photographed scene.",
  generateGeometry: "Adds simple primitives to the scene for staging and lighting tests.",
  metalEnv: "A separate HDR used only for metal reflections, so metal and stones can be lit independently.",
  ground: "Older floor controls: baked shadow on or off and a planar mirror reflection. Ground above covers the same floor.",
  groundShadowBake: "Older controls for the baked ground shadow: density, softness, quality and bake size.",
  studioPresets: "Ready-made studio rigs for the key, fill and ambient lights.",
  overallLighting: "A scene-wide brightness multiplier, and whether lights cast shadows onto the piece.",
  keyLight: "The main directional light. Off by default, because the environment does the lighting.",
  fillLight: "A secondary directional light for lifting the side away from the key.",
  hemiLight: "Flat ambient light graded from a sky colour above to a ground colour below.",
  sceneLights: "Add and edit extra directional, point, spot and area lights.",
  scenePresets: "Save the current look as a named scene snapshot and apply it again later.",

  // ── Anti-aliasing ──
  progressive: "Refines the image while the camera is still, averaging jittered frames into a clean, anti-aliased still.",
  taa: "Temporal anti-aliasing: smooths edges and sparkle while the camera moves.",
  velocity: "Per-pixel motion vectors used by TAA and the temporal reflection and shadow passes.",
  motion: "An optional resolution drop while orbiting heavy scenes, restored as soon as the camera stops.",

  // ── Post Processing ──
  ssr: "Real-time reflections of the piece in itself, such as the band reflecting its stones and prongs.",
  tonemapping: "Maps the HDR render to the screen: tone curve, exposure, contrast and saturation.",
  outline: "The selection and hover outline drawn in the editor. An editing aid, not part of the look.",
  chromatic: "Lens-style colour fringing toward the frame edges.",
  grain: "Film-grain noise over the final image.",
  lut: "Colour grading through look-up table images, up to three applied in order.",
  vignette: "Darkens or tints the corners of the frame.",
  dof: "Camera depth of field: a sharp focus band with blur in front and behind, with click-to-focus.",
  ssao: "Ambient occlusion: darkens crevices and contacts such as prong bases and pave beds for a grounded look.",
  ssbevel: "Treats sharp edges as slightly rounded for the AO and reflection passes.",
  bloom: "Glow around the brightest glints off polished metal and facets.",
  ssgi: "Experimental one-bounce coloured light between surfaces. Heavy, and off by default.",
  sscs: "Fine contact shadows where parts touch, beyond what AO catches.",
  gemRefraction: "Switches stones between the full internal ray-trace and cheap surface-only shading.",
  postMaster: "Master switch for the whole post-processing pipeline.",
  reflectionProbe: "A baked cubemap of the scene that can feed extra reflections to metals and diamonds.",
  reflectionMatrix: "Per-object control of how strongly each part reflects each other part.",
  debug: "Diagnostic views of the pipeline's internal buffers.",

  // ── Export ──
  branding: "A text watermark over the viewer and exported stills.",
  assetExport: "Export the scene or model as GLB, optionally Draco-compressed and with the scene settings embedded.",
  video: "Record the canvas to video: turntables, camera-view tours and glTF animations.",
  imageExport: "High-resolution stills with progressive convergence, tiling for very large sizes, and crop.",
  s3: "Send an export to your workspace storage, or your own connected bucket, and get a URL back.",
  shareLink: "Create shareable links to the current scene, for the editor or a viewer embed.",
  renderQuality: "Resolution of the live viewer: a quality tier or an exact pixel ratio.",
  sceneSettings: "Save or open the whole scene set-up as one file, or the ring with its scene inside. Older .vjson and settings files still open.",
  metalMaterial: "Save or load the selected part's metal material as a file.",
  diamondMaterial: "Save or load the selected stone's gem material as a file.",

  // ── Animations ──
  "gltf-clips": "Play the animation clips embedded in the loaded glTF or GLB.",
  "camera-views": "Saved camera viewpoints and animated transitions between them, for tours and turntables.",
  "interaction-prompt": "The on-screen drag-to-rotate hand hint that appears after the viewer sits idle.",
  scrollCamera: "Developer preview for binding camera views to page scroll in an embed. Not a viewer feature.",
  physics: "Rigid-body physics for dropping and colliding objects.",
  virtualCameras: "Named camera poses you can fly to by name, separate from the Camera Views tour.",
  sceneLoop: "Kiosk mode: cycles through models or saved scene presets on a timer.",
  posePlugin: "Auto-orients the model and rests it on the ground.",
  timeline: "Keyframe animation of the camera, parts and material properties on a timeline.",

  // ── Modifiers ──
  objectRotations: "Duplicates an object N times around a pivot, e.g. to build a circle of stones.",
  extrudeTube: "Extrudes a 2D profile along a circular path to make bands or wires.",
  simpleText: "Renders text into a texture and applies it to the selected material as colour, bump or alpha, for engraving and labels.",
  parallaxMapping: "Fakes surface depth from a height map without adding geometry.",
  parallaxCamera: "Subtle pointer or device-tilt camera motion for embeds.",
  simplify: "Reduces triangle count to lighten a model for performance or export.",

  // ── Configurators ──
  variationConfigurator: "Named product variations that swap materials or whole parts: the sets a shopper switches between.",
  materialConfigurator: "Switchable material options per part, e.g. a metal slot offering yellow, white or rose gold.",
  switchNode: "Switchable geometry: show one of several alternative parts, such as prong styles or halo on and off.",
  khr: "Material variants authored into the GLB with the glTF KHR_materials_variants extension.",

  // ── Plugins ──
  loadingScreen: "The loading overlay shown while assets load.",
  fullScreen: "Enter or leave full-screen viewing.",
  frameFade: "Cross-fades the image on big changes, such as a material or environment swap, instead of a hard cut.",
  rhinoLoader: "Import options for Rhino .3dm files exported from Rhino or MatrixGold.",
  modelStage: "Staging props and placeholders (stands, busts, hands) for composing a product shot.",
  watchHands: "Sets watch hands to a time, matching the hand meshes by name.",
  css3d: "Overlays HTML content, such as an iframe, positioned in 3D space.",
  materialExtensionToggles: "Read-only list of material extensions, all enabled. Edit them per part in Materials › Material extensions.",
  snowFall: "Decorative falling-snow particles.",
  waveGround: "An animated, rippling ground surface.",
  rainbowDiamond: "Tints a group of stones across a rainbow of colours, picked by name prefix.",
  extras: "Maintenance actions: clear saved editor preferences, clear viewer caches, and re-centre every mesh's geometry.",
};

/**
 * Hover help per setting, keyed `${sectionId}.${rowId}`. `text`: 1–2 short sentences,
 * starting with what it does and what HIGHER values (or "on") look like on a jewellery
 * render; mention cost when real ("GPU heavy", "recompiles shaders"). `default`: the
 * iJewel / engine default as a display string when you can VERIFY it (from
 * docs/verify/ijewel-field-parity-checklist.md "default X [min..max]" lines, or the viewer's
 * own defaults in ../webgl-jewelry-viewer/src), otherwise omit it.
 */
export const ROW_HELP: Record<string, { text: string; default?: string }> = {
  // ── Viewer › Camera ──
  "camera.fov": {
    text: "Vertical field of view in degrees. Lower reads as a longer lens with flatter, less distorted proportions (the product-shot look); higher widens the view and exaggerates perspective.",
    default: "25",
  },
  "camera.fit": {
    text: "Fit to View reframes the camera on the whole model. Zoom in and Zoom out move the camera about 15% closer to, or 18% farther from, the orbit target.",
  },
  "camera.presets": {
    text: "Jumps to a standard angle around the model centre at the last fitted distance. Top goes as close to straight down as Orbit V Min allows.",
  },
  "camera.poseExtras": {
    text: "Macro is the 3/4 view pulled 40% closer, Low angle mirrors the camera below the target to look up, and Tilt swings the view about 25 degrees around the vertical. Orbit V Max (90 by default) stops Low angle at the horizon unless raised.",
  },
  "camera.orbit-vmin": {
    text: "Smallest vertical orbit angle, in degrees from straight above. Raise it to stop users orbiting over the top of the piece.",
    default: "11.5° (iJewel 0°)",
  },
  "camera.orbit-vmax": {
    text: "Largest vertical orbit angle, in degrees from straight above. 90 keeps the camera at or above the horizon; raise it to allow views from underneath.",
    default: "90° (iJewel 180°)",
  },
  "camera.save-restore": {
    text: "Save remembers the current camera position and target; Restore returns to it. Kept for this session only, not saved with the scene.",
  },

  // ── Viewer › Drop Options ──
  "drop-options.autoCenter": {
    text: "Moves a dropped or opened model so its bounding-box centre sits at the world origin. Applies to new imports only.",
    default: "Off (iJewel on)",
  },
  "drop-options.autoScale": {
    text: "Uniformly rescales dropped or opened models so their bounding sphere matches Target Radius. Use it for CAD exports in millimetres or arbitrary units.",
    default: "Off (iJewel on)",
  },
  "drop-options.autoScaleRadius": {
    text: "Bounding-sphere radius, in scene units, that Auto Scale sizes new imports to. Only used while Auto Scale is on.",
    default: "2",
  },
  "drop-options.auto-place": {
    text: "Both buttons refit the camera to the loaded model, exactly like Fit to View. They do not move or rescale geometry; the checkboxes above do that at import.",
  },

  // ── Picking › Material (metal) ──
  "material.color": {
    text: "Base colour of the selected metal, i.e. the tint of its reflections (yellow, rose or white gold). Pure metal has no diffuse colour, so this is its whole hue.",
  },
  "material.roughness": {
    text: "Micro-surface roughness. 0 is a mirror polish with sharp reflections; higher values blur reflections toward satin, brushed and then matte.",
  },
  "material.metalness": {
    text: "1 is pure metal: tinted reflections and no diffuse shading. Lower values blend in a non-metallic, plastic-like layer; real precious metals stay at 1.",
  },
  "material.envMapIntensity": {
    text: "Strength of the environment reflection on this material, the main brightness control under the default environment-only lighting. The scene Env Intensity multiplies on top.",
  },
  "material.ior": {
    text: "Index of refraction for the non-metallic share of the surface. At Metalness 1 it has almost no visible effect.",
  },
  "material.clearcoat": {
    text: "Adds a clear lacquer layer with its own reflection over the metal; higher gives a second, sharper highlight on top. Moving it off 0 recompiles the shader.",
  },
  "material.clearcoatRoughness": {
    text: "Blur of the clearcoat reflection: 0 is a glassy lacquer, higher spreads the top highlight. No effect while Clearcoat is 0.",
  },
  "material.specularIntensity": {
    text: "Strength of the non-metallic specular reflection. Like IOR, it only shows where Metalness is below 1.",
  },
  "material.sheen": {
    text: "Soft, velvet-like glow at grazing angles, tinted by the sheen colour in Physical Material. That colour starts black, so this alone may show nothing; recompiles the shader.",
  },
  "material.anisotropy": {
    text: "Stretches highlights along one surface direction for a brushed-metal look; higher gives longer streaks. Recompiles the shader.",
  },

  // ── Picking › Diamond / Gem Material ──
  "diamondRayTracing.color": {
    text: "Body colour of the stone: light is absorbed toward it along its path inside, so it deepens with stone size and Absorption. White is colourless.",
    default: "White",
  },
  "diamondRayTracing.bounces": {
    text: "Internal ray bounces traced per pixel, up to 16. More bounces show more internal reflections and brilliance, especially in deep cuts; each adds GPU cost on every stone pixel.",
    default: "5",
  },
  "diamondRayTracing.ior": {
    text: "Refractive index, driving both the bending of light and the strength of the surface reflection. Higher traps more light inside and brightens the crown; real diamond is 2.42.",
    default: "2.6",
  },
  "diamondRayTracing.dispersion": {
    text: "Spread between red and blue refraction as light leaves the stone: the rainbow fire. 0 gives no colour flashes; above 0.001 each exit costs two extra environment samples.",
    default: "0.01",
  },
  "diamondRayTracing.reflectivity": {
    text: "Strength of the first-surface reflection off the facets facing the camera. Higher gives brighter, glassier table and crown reflections.",
    default: "0.5",
  },
  "diamondRayTracing.envMapIntensity": {
    text: "Brightness of the diamond environment for everything the stone shows, surface reflections and light returning from inside. Separate from the scene Env Intensity.",
    default: "1.3",
  },
  "diamondRayTracing.sparkleBoost": {
    text: "Multiplies the refractive index at internal exits. Above 1 traps more light by total internal reflection for more contrast and sparkle; below 1 the stone reads more see-through.",
    default: "1",
  },
  "diamondRayTracing.absorptionFactor": {
    text: "How strongly the body colour absorbs light per unit of path inside the stone. Higher deepens coloured stones; no effect on a pure white stone.",
    default: "1",
  },
  "diamondRayTracing.gammaFactor": {
    text: "Power curve on the stone's final colour. Above 1 darkens mid-tones for more contrast, below 1 lifts and flattens them; 1 is neutral.",
    default: "1",
  },
  "diamondRayTracing.squashFactor": {
    text: "Vertically squashes the proxy sphere the ray-trace uses to find internal hits before reading the baked facet map. Fits flatter or taller cuts; it moves the internal pattern rather than the brightness.",
    default: "0.98",
  },
  "diamondRayTracing.geometryFactor": {
    text: "Scales how far along each ray the proxy-sphere hit is placed before the facet lookup. Changes where internal reflections land, i.e. the stone's pattern, not its overall brightness.",
    default: "0.5",
  },
  "diamondRayTracing.surfaceReflBoost": {
    text: "Extra multiplier on the first-surface reflection. Higher makes the outside of the stone more mirror-like; 1 is unchanged.",
    default: "1",
  },
  "diamondRayTracing.internalEnvBoost": {
    text: "Multiplier on light returning from inside the stone after its bounces: brilliance and fire. Higher makes the interior glow brighter; 1 is unchanged.",
    default: "1",
  },
  "diamondRayTracing.envRotation": {
    text: "Spins the diamond environment around the vertical axis, in radians, moving where the bright flashes land. While Env Follow Camera is above 0, orbiting resets it to the shared gem rotation.",
    default: "0",
  },
  "diamondRayTracing.boostR": {
    text: "Red multiplier on light refracted through the stone. Raising it tints the fire and brilliance red; all three at 1 is neutral.",
    default: "1",
  },
  "diamondRayTracing.boostG": {
    text: "Green multiplier on light refracted through the stone. Raising it tints the fire and brilliance green; all three at 1 is neutral.",
    default: "1",
  },
  "diamondRayTracing.boostB": {
    text: "Blue multiplier on light refracted through the stone. Raising it tints the fire and brilliance blue; all three at 1 is neutral.",
    default: "1",
  },
  "diamondRayTracing.colorR": {
    text: "Red channel of the body colour, up to 2. Above 1 amplifies red along the internal path instead of absorbing it, which is how saturated ruby looks are made.",
    default: "1",
  },
  "diamondRayTracing.colorG": {
    text: "Green channel of the body colour, up to 2. Above 1 amplifies green along the internal path instead of absorbing it, as in the emerald preset.",
    default: "1",
  },
  "diamondRayTracing.colorB": {
    text: "Blue channel of the body colour, up to 2. Above 1 amplifies blue along the internal path instead of absorbing it, as in the sapphire preset.",
    default: "1",
  },
  "diamondRayTracing.opacity": {
    text: "Stone opacity. Below 1 blends the stone with what is behind it and switches it to transparent rendering; keep 1 for real stones.",
    default: "1",
  },

  // ── Scene › Background / Environment ──
  "environment.background": {
    text: "Flat backdrop colour behind the model. Picking one replaces an environment or image backdrop; clearing it makes the canvas transparent.",
    default: "#ffffff",
  },
  "environment.tonemapBg": {
    text: "On runs the backdrop through the same tone mapping as the model, the filmic iJewel look where white reads light grey. Off shows the picked colour exactly.",
    default: "Off",
  },
  "environment.tonemapGround": {
    text: "Tone-maps the floor pixels (shadow and reflection) as well. Keep it matched to Tonemap Background or a seam shows at the horizon.",
    default: "Off",
  },
  "environment.backgroundIntensity": {
    text: "Brightness of the backdrop, whether colour, image or environment; above 1 pushes a colour backdrop toward white. Light Multiplier scales it too.",
    default: "1",
  },
  "environment.envIntensity": {
    text: "Strength of the environment (HDR image-based lighting) on metals and other PBR materials; higher gives brighter reflections everywhere. Diamonds use their own environment and ignore it.",
    default: "1",
  },
  "environment.envUrl": {
    text: "Loads an HDR or EXR from a URL as the scene environment. It also retires the built-in metal HDR, so metals follow the new environment too.",
  },
  "environment.envFile": {
    text: "Loads a local HDR or EXR file as the scene environment. Like the URL loader, it retires the built-in metal HDR so metals follow it.",
  },
  "environment.showEnvBg": {
    text: "Shows the environment image itself behind the model instead of the flat colour. Handy for seeing how the HDR sits while rotating it.",
    default: "Off",
  },
  "environment.bgBlur": {
    text: "Blurs an environment, image or gradient backdrop; higher gives a softer, out-of-focus backdrop. A flat colour has nothing to blur.",
    default: "0",
  },
  "environment.envFollow": {
    text: "Turns the environment with the camera, for metal and diamond reflections. 0 keeps it fixed in the world so highlights slide over the piece as you orbit; 1 locks them in place on screen.",
    default: "0",
  },
  "environment.envRotation": {
    text: "Rotates the scene environment in radians; Y spins it around the piece to move highlights and reflection bands. Metals on a Metal HDR (the built-in one included) turn with Metal Rotation instead, and diamonds with their own rotation.",
    default: "0, 0, 0",
  },

  // ── Scene › Generate Geometry ──
  "generateGeometry.primitives": {
    text: "Adds a plane, box, sphere or circle to the scene with a grey polished-metal material. Useful for staging and checking lighting.",
  },
  "generateGeometry.primitives2": {
    text: "Adds a torus or cylinder the same way. Gem Sphere adds a sphere named as a gem, so it gets the diamond ray-trace shader.",
  },

  // ── Scene › Metal HDR (override) ──
  "metalEnv.metalEnvUrl": {
    text: "Loads an HDR or EXR from a URL used only for metal reflections, overriding the scene environment for metals. Lets you light metal and stones independently.",
  },
  "metalEnv.metalEnvFile": {
    text: "Loads a local HDR or EXR file used only for metal reflections, overriding the scene environment for metals.",
  },
  "metalEnv.metalEnvRotation": {
    text: "Rotates the metal HDR in radians, to move its bright lights off awkward parts of the band's curve. Only affects metals using the metal HDR.",
    default: "0, 0, 0",
  },
  "metalEnv.metalEnvCap": {
    text: "Clamps the brightest pixels of the metal HDR before it is prefiltered. Lower tames harsh white hotspots on polished metal; the top of the range removes the cap.",
    default: "16",
  },

  // ── Scene › Ground & Reflection (Legacy) ──
  "ground.contactShadow": {
    text: "Turns the baked ground shadow under the piece on or off. The same switch as Enable Shadow below and Baked Shadows in Ground.",
    default: "On",
  },
  "ground.reflection": {
    text: "Mirror reflection of the piece in the floor, a planar reflection that works over any backdrop. Costs an extra scene render whenever the camera moves.",
    default: "Off",
  },
  "ground.reflectionStrength": {
    text: "Opacity of the floor reflection; higher reads as polished stone or glass. No effect while Reflection is off.",
    default: "0.85",
  },
  "ground.reflectionBlur": {
    text: "Softens the floor reflection, from a sharp mirror at 0 to a diffuse, glossy smear at 1.",
    default: "0.5",
  },

  // ── Scene › Ground Shadow (Baked) (Legacy) ──
  "groundShadowBake.enable": {
    text: "Turns the baked ground shadow on or off. The same flag as Contact Shadow above and Baked Shadows in Ground.",
    default: "On",
  },
  "groundShadowBake.darkness": {
    text: "Opacity multiplier of the baked ground shadow. Higher gives a denser, darker pool; above 1 over-darkens.",
    default: "1",
  },
  "groundShadowBake.softness": {
    text: "Width of the virtual light used for the bake: 0 is a hard sun with crisp edges, 1 a whole-sky light with a broad, soft pool. Changing it re-bakes.",
    default: "0.5",
  },
  "groundShadowBake.falloff": {
    text: "Power curve on shadow density. Above 1 keeps a dense core at the contact and fades the tail faster; below 1 spreads a more even, greyer pool.",
    default: "1.25",
  },
  "groundShadowBake.blur": {
    text: "Blur radius, in shadow-map texels, over the baked shadow. Higher smooths grain and edges; 0 turns it off.",
    default: "2",
  },
  "groundShadowBake.quality": {
    text: "Jittered light samples in a finished bake. More give a smoother, less banded shadow but take longer to converge on idle.",
    default: "160",
  },
  "groundShadowBake.spread": {
    text: "Size of the shadow plane as a multiple of the model's largest dimension. Raise it if a long, soft shadow is cut off at the edge.",
    default: "1.5",
  },
  "groundShadowBake.resolution": {
    text: "Resolution of the shadow bake texture. Higher keeps fine contact detail under prongs and thin shanks, at more memory and bake time.",
    default: "512",
  },
  "groundShadowBake.rebake": {
    text: "Restarts the ground-shadow bake. It already re-bakes by itself when the model or these settings change; orbiting never triggers it.",
  },
  "groundShadowBake.lightHelper": {
    text: "Shows a gizmo for the shadow-casting light's direction, as a debugging aid. The same switch as Tools › Helpers › Visible.",
    default: "Off",
  },

  // ── Scene › Lighting ──
  "overallLighting.lightMultiplier": {
    text: "Master multiplier over the scene environment, the backdrop and every light, to brighten or dim the whole scene at once. Diamonds use their own environment and are not affected.",
    default: "1",
  },
  "overallLighting.selfShadows": {
    text: "Lets lights cast shadows onto the jewellery itself, such as prongs onto the band. Needs a lit shadow-casting light (the key light starts at 0) and costs a shadow-map render.",
    default: "Off",
  },
  "keyLight.keyIntensity": {
    text: "Strength of the main directional light. The default look is lit by the environment alone, so this starts at 0; raise it for a hard highlight accent or self-shadows.",
    default: "0",
  },
  "keyLight.keyDirection": {
    text: "Direction from the model toward the key light. Moves its highlights and, with Self Shadows on, the shadows it casts on the piece.",
    default: "-0.66, 0.64, 0.38",
  },
  "keyLight.keyColor": {
    text: "Colour of the key light; a warm tint warms the highlights it creates. No visible effect while its intensity is 0.",
    default: "#fff8ee",
  },
  "fillLight.fillIntensity": {
    text: "Strength of the secondary directional light that lifts the side away from the key. Starts at 0 under environment-only lighting.",
    default: "0",
  },
  "fillLight.fillDirection": {
    text: "Direction from the model toward the fill light; moves the highlights it adds.",
    default: "0.76, 0.53, -0.38",
  },
  "fillLight.fillColor": {
    text: "Colour of the fill light. No visible effect while its intensity is 0.",
    default: "#f5ede0",
  },
  "hemiLight.hemiIntensity": {
    text: "Strength of a flat ambient light graded from the sky colour above to the ground colour below. It only adds diffuse light, so pure metal and diamonds barely change while non-metal parts lift.",
    default: "0",
  },
  "hemiLight.hemiSky": {
    text: "Ambient colour received by surfaces facing up.",
    default: "#fff8ee",
  },
  "hemiLight.hemiGround": {
    text: "Ambient colour received by surfaces facing down, such as the underside of the band.",
    default: "#e8e0d2",
  },

  // ── Anti-aliasing ──
  "progressive.enabled": {
    text: "After the camera stops, keeps rendering sub-pixel-jittered frames and averages them for clean edges and noise-free reflections and AO, then stops. Off leaves the first, noisier frame.",
    default: "On",
  },
  "progressive.maxFrames": {
    text: "Frames averaged before refinement stops. More converge to a cleaner still but take longer to settle after every change.",
    default: "32",
  },
  "progressive.jitter": {
    text: "Shifts each refinement frame by a sub-pixel offset, so the average anti-aliases edges. Off still averages out noise, but facet and silhouette edges stay stair-stepped.",
    default: "On",
  },
  "taa.enabled": {
    text: "Temporal anti-aliasing (TAA): blends each moving frame with reprojected history to smooth edges and sparkle while orbiting. Idle frames use Progressive instead.",
    default: "On",
  },
  "taa.feedbackMin": {
    text: "History weight for fast-moving pixels. Higher is smoother but ghosts and smears on quick orbits; lower is sharper and noisier.",
    default: "0.88",
  },
  "taa.feedbackMax": {
    text: "History weight for pixels that are barely moving. Higher gives calmer, more stable edges, at the cost of a slight lag when motion starts.",
    default: "0.97",
  },
  "taa.stableNoise": {
    text: "Seeds reflection and contact-shadow noise from an ever-advancing counter while moving, so TAA averages it away. Off repeats the same pattern every frame, a fixed stipple during orbit.",
    default: "On",
  },
  "velocity.enabled": {
    text: "Renders per-pixel motion vectors even with TAA off, e.g. for the velocity debug view. TAA and the temporal passes render them automatically.",
    default: "Off",
  },
  "motion.enabled": {
    text: "Lowers the internal render resolution step by step while a heavy scene is orbited, restoring it on idle. Smoother motion, at the cost of softness while moving.",
    default: "Off",
  },
  "motion.minScale": {
    text: "Lowest render scale the motion ladder may drop to. Lower keeps orbiting fluid on weak GPUs but looks blurrier in motion; 1 disables the drop.",
    default: "0.85",
  },

  // ── Post Processing › Screen Space Reflections ──
  "ssr.enabled": {
    text: "Screen-space reflections (SSR): metal reflects other visible parts of the piece, such as the band reflecting its stones and prongs. GPU heavy; off leaves only environment reflections.",
    default: "On",
  },
  "ssr.intensity": {
    text: "How strongly traced reflections replace the environment reflection on metal. Above 1 pushes weak, low-confidence hits to full strength.",
    default: "1",
  },
  "ssr.steps": {
    text: "Samples marched along each reflection ray. More catch thin features like prongs and reduce banding; cost rises linearly.",
    default: "16",
  },
  "ssr.refineSteps": {
    text: "Intended hit-refinement steps after a ray finds a surface. The reflection shader refines hits by interpolation instead and never reads this, so it has no visible effect.",
    default: "6",
  },
  "ssr.maxDistance": {
    text: "Travel distance, in model radii, over which reflections fade out. Higher keeps far parts of the piece at full strength in reflections.",
    default: "3",
  },
  "ssr.thickness": {
    text: "Intended hit thickness, in model radii. The reflection shader never reads it (hit acceptance uses Tolerance), so it has no visible effect.",
    default: "0.05",
  },
  "ssr.boost": {
    text: "Per-channel (R, G, B) multiplier on the reflected colour before it is composited. Above 1 brightens or tints reflections in that channel.",
    default: "1, 1, 1",
  },
  "ssr.objectRadius": {
    text: "Fixed reflection ray length as a multiple of the model radius, used while Auto radius is off. Longer rays reach farther parts but spread the same steps thinner.",
    default: "1",
  },
  "ssr.autoRadius": {
    text: "Picks each ray's length from the camera distance and ray direction, within 0.1-5x Object Radius. Off uses Object Radius as a fixed length.",
    default: "On",
  },
  "ssr.power": {
    text: "Exponent on hit confidence. Higher fades weak or grazing hits more for cleaner but sparser reflections; lower keeps more of them.",
    default: "1.1",
  },
  "ssr.tolerance": {
    text: "Depth window for accepting a hit. Higher finds more hits (fewer holes) but can smear reflections onto the wrong surface; lower is stricter.",
    default: "0.5",
  },
  "ssr.lowQualityFrames": {
    text: "The first N refinement frames trace at half the step count, so the first response after a change is quicker and slightly coarser.",
    default: "0",
  },
  "ssr.maskFrontRays": {
    text: "Fades reflections on surfaces facing the camera head-on, which would need to show what is behind the camera and cannot in screen space.",
    default: "On",
  },
  "ssr.maskFront": {
    text: "Shifts that fade: higher (toward 1) keeps more reflection on camera-facing surfaces, lower masks more. Only used while Ignore front rays is on.",
    default: "-0.2",
  },
  "ssr.rough": {
    text: "How far refinement frames scatter rays around the mirror direction on rough metal. 1 converges to physically blurred glossy reflections on idle; 0 keeps every surface mirror-sharp.",
    default: "1",
  },
  "ssr.maxRough": {
    text: "Surfaces rougher than this skip tracing and keep their environment reflection. Raise it to trace satin and brushed finishes too, at more cost and noise.",
    default: "0.7",
  },
  "ssr.fresnel": {
    text: "Fresnel weighting: reflections fade when viewed head-on and strengthen toward grazing angles, as on real metal.",
    default: "On",
  },
  "ssr.halfRes": {
    text: "Traces reflections at half resolution with a depth-aware upscale. A quarter of the traced pixels, with softer reflection detail.",
    default: "Off (on for mobile)",
  },
  "ssr.wBackground": {
    text: "Weight of the environment in reflection rays that miss geometry. It only acts on the reflection-probe fill for missed rays, which is off by default, so normally there is no visible change.",
    default: "1",
  },
  "ssr.wMetal": {
    text: "How strongly metal reflects other metal. 0 removes metal from reflections; above 1 over-emphasises it.",
    default: "1",
  },
  "ssr.wGem": {
    text: "How strongly metal reflects diamonds and gems. Lower removes bright stone reflections from the band; the stones themselves are unchanged.",
    default: "1",
  },
  "ssr.wGround": {
    text: "How strongly metal reflects the floor, including its shadow and reflection. 0 removes the floor from reflections.",
    default: "1",
  },

  // ── Post Processing › Tonemapping ──
  "tonemapping.tonemap": {
    text: "Curve that maps HDR light into screen range: ACES Filmic rolls highlights off smoothly, Linear clips, None clips and ignores Exposure. Only tone-mapped pixels change: the model, plus the backdrop and floor if their flags are on.",
    default: "ACES Filmic",
  },
  "tonemapping.exposure": {
    text: "Brightness multiplier applied before tone mapping; higher brightens the model and blows out highlights sooner. Ignored by the None curve.",
    default: "1",
  },
  "tonemapping.tonemapBg": {
    text: "On applies tone mapping and grading to the backdrop too, so a white backdrop reads light grey. Off keeps the picked colour exact.",
    default: "Off",
  },
  "tonemapping.tonemapGround": {
    text: "Tone-maps the floor pixels as well. Keep it matched to Tonemap Background or the frame seams at the horizon.",
    default: "Off",
  },
  "tonemapping.clipBg": {
    text: "Meant to clip backdrop values above 1 before display. Here an un-tone-mapped backdrop is already clamped and a tone-mapped one ignores the flag, so it has no visible effect.",
    default: "Off",
  },
  "tonemapping.renderDepth": {
    text: "Shows the depth buffer instead of the image, near surfaces bright and far ones fading to black. A diagnostic view.",
    default: "Off",
  },
  "tonemapping.contrast": {
    text: "Contrast around mid-grey after tone mapping; above 1 deepens shadows and punches highlights. Applies only to tone-mapped pixels.",
    default: "1",
  },
  "tonemapping.saturation": {
    text: "Colour saturation after tone mapping; above 1 richens gold and coloured stones, 0 is greyscale. Applies only to tone-mapped pixels.",
    default: "1",
  },

  // ── Post Processing › Lens and grading effects ──
  "chromatic.enabled": {
    text: "Lens-style colour fringing: red and blue shift apart toward the frame edges, with none at the centre.",
    default: "Off",
  },
  "chromatic.intensity": {
    text: "Width of the fringes, growing with the square of the distance from the centre. Small values read as a real lens, large ones as a glitch.",
    default: "0.01",
  },
  "grain.enabled": {
    text: "Film-grain noise over the final image. Its pattern only changes when the view restarts, so idle refinement does not average it away.",
    default: "Off",
  },
  "grain.intensity": {
    text: "Grain amplitude; each unit adds up to 0.5% of full brightness as noise.",
    default: "10",
  },
  "grain.multiply": {
    text: "Modulates brightness by the grain instead of adding it, so dark areas stay clean and grain shows mostly in highlights.",
    default: "Off",
  },
  "lut.enabled": {
    text: "Colour grading through look-up tables (LUTs, images that remap colours), up to three slots applied in order.",
    default: "Off",
  },
  "lut.intensity": {
    text: "Blend between the ungraded image at 0 and the fully graded one at 1.",
    default: "1",
  },
  "lut.lutBackground": {
    text: "Also grades the backdrop. Off keeps the backdrop colour exact while the model is graded.",
    default: "On",
  },
  "vignette.enabled": {
    text: "Darkens, or tints, the image toward the corners to hold attention on the piece.",
    default: "Off",
  },
  "vignette.power": {
    text: "Curve of the corner falloff; higher darkens more of the frame from the corners inward.",
    default: "0.5",
  },
  "vignette.darkness": {
    text: "How far the fade reaches toward the centre; higher darkens a larger area.",
    default: "0.35",
  },
  "vignette.offset": {
    text: "Shapes the fade's edge: low values start a long gradient near the centre, high values keep the centre clean with a harder edge.",
    default: "0.6",
  },
  "vignette.color": {
    text: "Colour the corners fade to. Black darkens; white or a tint gives a soft halo frame.",
    default: "#000000",
  },

  // ── Post Processing › Depth of Field ──
  "dof.enabled": {
    text: "Depth of field: keeps a band of depth sharp and blurs what is nearer and farther, like a macro lens.",
    default: "Off",
  },
  "dof.focus": {
    text: "Focuses on whatever is at the centre of the frame, or on the orbit centre if nothing is there.",
  },
  "dof.enableEdit": {
    text: "While on, clicking the model sets the focus point there without blocking orbit. The point is held in world space, so it stays sharp as you orbit.",
    default: "Off",
  },
  "dof.depthRange": {
    text: "Depth of the in-focus band as a multiple of the model radius. Higher keeps more of the piece sharp; lower gives a shallow, macro-style focus.",
    default: "1.5",
  },
  "dof.nearBlur": {
    text: "Blur strength in front of the focus band.",
    default: "0.25",
  },
  "dof.farBlur": {
    text: "Blur strength behind the focus band.",
    default: "0.25",
  },
  "dof.maxBlur": {
    text: "Largest blur radius, in pixels, at full strength. Higher allows bigger, creamier out-of-focus areas.",
    default: "16",
  },

  // ── Post Processing › SS Ambient Occlusion ──
  "ssao.enabled": {
    text: "Screen-space ambient occlusion (SSAO): darkens crevices and contact areas such as prong bases, pave beds and the inside of the band. Diamonds are never darkened.",
    default: "On",
  },
  "ssao.intensity": {
    text: "Strength of the occlusion darkening.",
    default: "0.25",
  },
  "ssao.autoRadius": {
    text: "Derives the sampling radius from the model size, using Radius (x model). Off uses an absolute world-space radius.",
    default: "On (iJewel off)",
  },
  "ssao.radiusScale": {
    text: "Sampling radius as a fraction of the model's bounding radius. Larger catches broad shading between parts; smaller keeps AO tight to contacts.",
    default: "0.5",
  },
  "ssao.radius": {
    text: "Sampling radius in world units, used while Auto Radius is off. Larger catches broad shading between parts; smaller keeps AO tight to contacts.",
    default: "0.5 (iJewel 1)",
  },
  "ssao.bias": {
    text: "Suppresses self-occlusion on flat or gently curved faces. Raise it if flat metal shows blotchy grey; too high loses subtle contact shading.",
    default: "0.001",
  },
  "ssao.falloff": {
    text: "How quickly occlusion weakens with distance from the shaded point. Higher confines darkening to tight contacts; lower spreads it wider.",
    default: "1.3",
  },
  "ssao.samples": {
    text: "Samples per pixel. More reduce noise in a single frame; cost scales with the count.",
    default: "11",
  },
  "ssao.edge": {
    text: "How strongly the AO blur respects depth and normal edges. Higher stops occlusion bleeding across edges but leaves more noise.",
    default: "0.1",
  },
  "ssao.ground": {
    text: "How much AO darkens the floor. 0 leaves the floor to the baked ground shadow.",
    default: "0",
  },
  "ssao.smooth": {
    text: "Runs an edge-aware blur over the AO so a single frame is smooth rather than speckled.",
    default: "On",
  },
  "ssao.halfRes": {
    text: "Computes AO at half resolution with a depth-aware upscale. Faster, with softer AO detail.",
    default: "Off (on for mobile)",
  },
  "ssao.cavity": {
    text: "Adds small-scale curvature occlusion that darkens fine concave detail such as engraving, milgrain and stone seats.",
    default: "Off",
  },
  "ssao.cavityIntensity": {
    text: "Strength of the cavity darkening; 1 matches the reference renderer.",
    default: "1",
  },
  "ssao.cavityBrightness": {
    text: "Lifts the cavity term back toward white; higher makes cavity darkening subtler.",
    default: "0.2",
  },

  // ── Post Processing › SSBevel ──
  "ssbevel.enabled": {
    text: "Screen-space bevel: rounds sharp edges in the normal buffer so AO, reflections and contact shadows treat creases as slightly rounded. The base shading is unchanged; costs some performance.",
    default: "Off",
  },
  "ssbevel.sceneBevel": {
    text: "Applies the bevel to the whole scene instead of only to materials with the SSBevel extension enabled.",
    default: "Off",
  },
  "ssbevel.radius": {
    text: "Reach of the edge rounding in pixels; higher softens wider creases.",
    default: "2",
  },

  // ── Post Processing › Bloom ──
  "bloom.enabled": {
    text: "Glow around very bright highlights, the glints off polished metal and facets. At the default threshold only true highlights bloom.",
    default: "On",
  },
  "bloom.threshold": {
    text: "Brightness above which pixels glow, in linear HDR before exposure (a white backdrop is about 1). Lower makes more of the piece glow.",
    default: "2",
  },
  "bloom.soft": {
    text: "Softens the threshold cut-off: 0 is a hard cut, higher lets glow fade in gradually below the threshold.",
    default: "0.5",
  },
  "bloom.intensity": {
    text: "Strength of the glow added to the image.",
    default: "0.2",
  },
  "bloom.iterations": {
    text: "Blur levels in the glow pyramid. More levels give wider halos.",
    default: "4",
  },
  "bloom.radius": {
    text: "Balances glow between the tight blur levels (0) and the wide ones (1). Higher gives a larger, softer halo.",
    default: "0.6",
  },
  "bloom.power": {
    text: "Curve distributing glow across the levels. Higher dims the glow and concentrates it on the levels Radius favours.",
    default: "1",
  },
  "bloom.debug": {
    text: "Shows only the bloom buffer, to see exactly which pixels glow.",
    default: "Off",
  },

  // ── Post Processing › SS Global Illumination ──
  "ssgi.enabled": {
    text: "Screen-space global illumination (SSGI): one bounce of coloured light between visible surfaces. Only non-metal surfaces receive it, never pure metal or gems; dev feature, GPU heavy.",
    default: "Off",
  },
  "ssgi.intensity": {
    text: "Strength of the bounced light added.",
    default: "2",
  },
  "ssgi.power": {
    text: "Exponent on hit confidence. Higher keeps only confident bounces, for a cleaner but weaker result.",
    default: "1.1",
  },
  "ssgi.objectRadius": {
    text: "Reach of the bounce rays: a multiple of the model radius with Auto radius on, otherwise world units.",
    default: "2",
  },
  "ssgi.falloff": {
    text: "How fast bounced light weakens with distance; higher keeps it local to contacts.",
    default: "0.7",
  },
  "ssgi.autoRadius": {
    text: "Scales Object Radius by the model size. Off treats it as absolute world units.",
    default: "On",
  },
  "ssgi.tolerance": {
    text: "Thickness window for accepting ray hits. Higher lets thin parts catch and cast bounce light, with more risk of leaks.",
    default: "1",
  },
  "ssgi.bias": {
    text: "Depth offset that stops a surface lighting itself. Raise it if flat faces glow with their own colour.",
    default: "0.002 (iJewel 0.15)",
  },
  "ssgi.rayCount": {
    text: "Rays per pixel per frame. More reduce noise; cost scales with the count.",
    default: "2",
  },
  "ssgi.stepCount": {
    text: "March steps per ray. More find more bounce sources; cost scales with the count.",
    default: "4",
  },
  "ssgi.smooth": {
    text: "Blurs the GI buffer with an edge-aware filter to hide noise.",
    default: "On",
  },
  "ssgi.renderWithCamera": {
    text: "Also computes GI while the camera moves. Off computes it only on idle refinement frames, keeping orbiting fast.",
    default: "On",
  },
  "ssgi.ssgiEnabled": {
    text: "Sub-switch for the GI term. Off drops the bounce light from the image while the pass itself stays enabled.",
    default: "On",
  },
  "ssgi.halfRes": {
    text: "Computes GI at half resolution. Much cheaper, and bounce light is soft enough that little is lost.",
    default: "On",
  },

  // ── Post Processing › Screen Space Contact Shadows ──
  "sscs.enabled": {
    text: "Screen-space contact shadows (SSCS): short-range darkening where parts touch, such as the line where a prong meets the band or between pave stones. Never darkens diamonds or the floor.",
    default: "Off",
  },
  "sscs.radius": {
    text: "Length of the contact rays as a multiple of the model radius. Higher spreads the darkening farther from each contact.",
    default: "0.05 (iJewel 0.015)",
  },
  "sscs.intensity": {
    text: "Strength of the contact darkening.",
    default: "1",
  },
  "sscs.tolerance": {
    text: "Thickness window for counting an occluder. Higher lets thin parts cast contact shadow, with more risk of dark halos.",
    default: "1.5",
  },
  "sscs.steps": {
    text: "Samples per contact ray. More give smoother, more accurate contacts; cost scales with the count.",
    default: "16 (iJewel 2)",
  },
  "sscs.onlyDebug": {
    text: "Shows only the contact-shadow buffer, to see exactly where it darkens.",
    default: "Off",
  },
  "sscs.halfRes": {
    text: "Computes contact shadows at half resolution. Faster, with slightly softer contacts.",
    default: "Off",
  },

  // ── Post Processing › Gem Refraction / master / probe / debug ──
  "gemRefraction.enabled": {
    text: "On traces light through every stone, with full internal bounces, fire and brilliance. Off shades stones by surface reflection only, cheaper but glassy and lifeless; toggling recompiles the gem shaders.",
    default: "On",
  },
  "postMaster.enabled": {
    text: "Master switch for the post pipeline: SSR, AO, bloom, progressive anti-aliasing, grading and the rest. Off renders one plain pass, much faster but without any of them.",
    default: "On",
  },
  "reflectionProbe.enabled": {
    text: "Bakes a cubemap of the whole scene from the model centre, re-baked only when the model or environment changes. On its own it changes nothing; the options below use it.",
    default: "Off",
  },
  "reflectionProbe.asSsrFallback": {
    text: "Meant to fill reflection rays that miss with the probe. It also needs the SSR probe-fallback flag, which is off by default and not exposed here, so normally there is no visible change.",
    default: "Off",
  },
  "reflectionProbe.asMetalEnv": {
    text: "Uses the probe as the metals' reflection environment. Only takes effect with no metal HDR loaded; the built-in metal HDR wins by default.",
    default: "Off",
  },
  "reflectionProbe.asGemEnv": {
    text: "Stones also reflect the probe, so a diamond shows its neighbouring stones and the ring on its crown. Turns the probe on and recompiles the gem shaders.",
    default: "Off",
  },
  "reflectionProbe.gemProbeIntensity": {
    text: "Strength of that probe reflection inside the stones.",
    default: "0.5",
  },
  "debug.view": {
    text: "Shows an internal buffer in place of the image: AO, SSR, normals, depth, bloom, accumulation, depth-of-field blur map, contact shadows, SSGI or bevel normals. For diagnosing effects.",
    default: "Off",
  },
  "debug.split": {
    text: "Horizontal position where the debug view starts. 0 shows it full screen; higher keeps the normal image on the left.",
    default: "0",
  },

  // ── Export ──
  "branding.wmOn": {
    text: "Draws the watermark text over the viewer and into exported stills. Video exports are not watermarked.",
    default: "Off",
  },
  "branding.wmText": {
    text: "The watermark text. Nothing is drawn while it is empty.",
  },
  "branding.wmLight": {
    text: "Uses light text for dark backdrops instead of the default dark text.",
    default: "Off",
  },
  "renderQuality.quality": {
    text: "Base resolution of the live viewer: Low 1x, Medium up to 2x device pixels, High up to 3x, Ultra 1.5x the device ratio. Higher is sharper, GPU cost grows with pixel count, and it resets Custom Pixel Ratio.",
    default: "Medium (up to 2x)",
  },
  "renderQuality.pixelRatio": {
    text: "Exact render pixels per screen pixel for the live viewer. Above the device ratio supersamples for crisper facets, below renders faster and softer; cost grows with the square of the ratio.",
    default: "Device ratio, max 2",
  },
  "sceneSettings.wholeScene": {
    text: "Whole scene saves everything except the ring as one .jscene with every file packed inside; Import replaces the scene with one exactly, as one Undo step, and also opens older .vjson and settings JSON files. Ring + scene saves the ring as a .glb with that whole scene inside, so opening it brings back the ring and its look.",
  },
  "metalMaterial.metal": {
    text: "Export saves the selected part's metal material as JSON; Import applies a saved one to it. Reports an error if the selection is not metal.",
  },
  "diamondMaterial.diamond": {
    text: "Export saves the selected stone's gem shader settings as JSON; Import applies saved ones to it. Reports an error if the selection is not a gem.",
  },

  // ── Animations ──
  "gltf-clips.reset": {
    text: "Stops the playing glTF clip and returns it to its first frame.",
  },
  "posePlugin.pose": {
    text: "Compute Pose auto-orients the model by its principal axes (longest axis horizontal), rests it on the ground and refits the camera. Drop to Ground only lowers it onto the floor, without rotating.",
  },
};
